import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

function ProgramFlags() {
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [filters, setFilters] = useState({
        status: '',
        category: '',
        phase: '',
        search: ''
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchFlags();
    }, []);

    const fetchFlags = async (page = 1) => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page,
                limit: 20,
                ...(filters.status && { status: filters.status }),
                ...(filters.category && { category: filters.category }),
                ...(filters.phase && { phase: filters.phase }),
                ...(filters.search && { search: filters.search }),
            });

            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/program-flags/list?${queryParams}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                setFlags(data.data.programFlags || []);
                setPagination(data.data.pagination || {});
            } else {
                toast.error(data.message || 'Failed to fetch program flags');
            }
        } catch (error) {
            console.error('Error fetching program flags:', error);
            toast.error('Error fetching program flags');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (id) => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/program-flags/${id}/toggle`,
                { method: 'PATCH' }
            );

            const data = await res.json();
            if (data.status === 'success') {
                toast.success(data.message);
                fetchFlags(pagination.page || 1);
            } else {
                toast.error(data.message || 'Toggle failed');
            }
        } catch (error) {
            console.error('Error toggling flag:', error);
            toast.error('Error toggling flag');
        }
    };

    const isPlanAllowed = (flag, plan) => {
        if (!flag.planRestrictions?.enabled) return true;
        return flag.planRestrictions.allowedPlans?.includes(plan);
    };

    const handleTogglePlan = async (id, plan) => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/program-flags/${id}/plan/${plan}`,
                { method: 'PATCH' }
            );

            const data = await res.json();
            if (data.status === 'success') {
                toast.success(data.message);
                fetchFlags(pagination.page || 1);
            } else {
                toast.error(data.message || 'Toggle failed');
            }
        } catch (error) {
            console.error('Error toggling plan access:', error);
            toast.error('Error toggling plan access');
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/program-flags/${deleteId}`,
                { method: 'DELETE' }
            );

            const data = await res.json();
            if (data.status === 'success') {
                toast.success('Program flag deleted successfully');
                setDeleteId(null);
                window.$?.('#modaldelete_flag').modal('hide');
                fetchFlags(pagination.page || 1);
            } else {
                toast.error(data.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Error deleting flag:', error);
            toast.error('Error deleting flag');
        } finally {
            setDeleting(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchFlags(1);
    };

    const handleClearFilters = () => {
        setFilters({ status: '', category: '', phase: '', search: '' });
        setTimeout(() => fetchFlags(1), 0);
    };

    const getCategoryBadge = (category) => {
        const colors = {
            program: 'badge-primary',
            feature: 'badge-info',
            integration: 'badge-success',
            ui: 'badge-warning'
        };
        return colors[category] || 'badge-secondary';
    };

    const getStatusBadge = (status) => {
        const colors = {
            active: 'badge-success',
            inactive: 'badge-secondary',
            deprecated: 'badge-danger'
        };
        return colors[status] || 'badge-secondary';
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Program Flags</h1>
                        </div>
                        <div className="col-sm-6 txt_rgt">
                            <Link to="/programFlagsAdd" className="btn btn-blue">
                                Add Program Flag
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    {/* Filters */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <form onSubmit={handleSearch} className="row align-items-end">
                                <div className="col-md-2">
                                    <label>Status</label>
                                    <select
                                        className="form-control"
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="deprecated">Deprecated</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label>Category</label>
                                    <select
                                        className="form-control"
                                        name="category"
                                        value={filters.category}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        <option value="program">Program</option>
                                        <option value="feature">Feature</option>
                                        <option value="integration">Integration</option>
                                        <option value="ui">UI</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label>Phase</label>
                                    <select
                                        className="form-control"
                                        name="phase"
                                        value={filters.phase}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        <option value="1">Phase 1</option>
                                        <option value="2">Phase 2</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label>Search</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="search"
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                        placeholder="Search by key, name..."
                                    />
                                </div>
                                <div className="col-md-3">
                                    <button type="submit" className="btn btn-primary mr-2">
                                        Filter
                                    </button>
                                    <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                                        Clear
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : flags.length > 0 ? (
                                    <>
                                        <table width="100%" className="table table-striped table-bordered txt_lft">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '50px' }}>Order</th>
                                                    <th>Key</th>
                                                    <th>Name</th>
                                                    <th style={{ width: '100px' }}>Category</th>
                                                    <th style={{ width: '80px' }}>Phase</th>
                                                    <th style={{ width: '80px' }}>Status</th>
                                                    <th style={{ width: '100px' }}>Enabled</th>
                                                    <th style={{ width: '80px' }}>Free</th>
                                                    <th style={{ width: '80px' }}>Premium</th>
                                                    <th style={{ width: '120px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {flags.map((flag) => (
                                                    <tr key={flag._id}>
                                                        <td>{flag.displayOrder}</td>
                                                        <td>
                                                            <code>{flag.key}</code>
                                                        </td>
                                                        <td>
                                                            <strong>{flag.name}</strong>
                                                            {flag.description && (
                                                                <br />
                                                            )}
                                                            {flag.description && (
                                                                <small className="text-muted">
                                                                    {flag.description.length > 60
                                                                        ? `${flag.description.substring(0, 60)}...`
                                                                        : flag.description}
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${getCategoryBadge(flag.category)}`}>
                                                                {flag.category}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="badge badge-outline-primary">
                                                                Phase {flag.phase}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${getStatusBadge(flag.status)}`}>
                                                                {flag.status}
                                                            </span>
                                                        </td>
                                                        <td align="center">
                                                            <div className="toggle-switch">
                                                                <label className="switch">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={flag.isGloballyEnabled}
                                                                        onChange={() => handleToggle(flag._id)}
                                                                    />
                                                                    <span className="slider"></span>
                                                                </label>
                                                                <span className="toggle-label">
                                                                    {flag.isGloballyEnabled ? 'On' : 'Off'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td align="center">
                                                            <div className="toggle-switch">
                                                                <label className="switch">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isPlanAllowed(flag, 'free')}
                                                                        onChange={() => handleTogglePlan(flag._id, 'free')}
                                                                    />
                                                                    <span className="slider"></span>
                                                                </label>
                                                            </div>
                                                        </td>
                                                        <td align="center">
                                                            <div className="toggle-switch">
                                                                <label className="switch">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isPlanAllowed(flag, 'premium')}
                                                                        onChange={() => handleTogglePlan(flag._id, 'premium')}
                                                                    />
                                                                    <span className="slider"></span>
                                                                </label>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div align="center">
                                                                <Link to={`/programFlagsView/${flag._id}`} className="crsr_pntr">
                                                                    <i className="fa-solid fa-eye mr-2" title="View" />
                                                                </Link>
                                                                <Link to={`/programFlagsAdd/${flag._id}`} className="crsr_pntr">
                                                                    <i className="fa-solid fa-pen mr-2" title="Edit" />
                                                                </Link>
                                                                <button
                                                                    className="crsr_pntr btn btn-link p-0"
                                                                    onClick={() => setDeleteId(flag._id)}
                                                                    data-toggle="modal"
                                                                    data-target="#modaldelete_flag"
                                                                >
                                                                    <i className="fa-solid fa-trash-can" title="Delete" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Pagination */}
                                        {pagination.pages > 1 && (
                                            <nav className="mt-3">
                                                <ul className="pagination justify-content-center">
                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchFlags(pagination.page - 1)}
                                                            disabled={pagination.page === 1}
                                                        >
                                                            Previous
                                                        </button>
                                                    </li>
                                                    {[...Array(pagination.pages)].map((_, i) => (
                                                        <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => fetchFlags(i + 1)}
                                                            >
                                                                {i + 1}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchFlags(pagination.page + 1)}
                                                            disabled={pagination.page === pagination.pages}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center p-5">
                                        <p>No program flags found</p>
                                        <Link to="/programFlagsAdd" className="btn btn-primary">
                                            Create your first flag
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            <div className="modal fade" id="modaldelete_flag" tabIndex="-1">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Confirm Delete</h5>
                            <button type="button" className="close" data-dismiss="modal">
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this program flag?</p>
                            <p className="text-muted small">
                                This action cannot be undone. Features controlled by this flag may be affected.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-dismiss="modal">
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDelete}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ProgramFlags
